import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Modal, Form, Input, Select, Spin, message } from 'antd';
import { UserPlus, Edit2, Trash2 } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { USERS, ROLES } from '../../constants/api';
import { ROLE_TYPE_LABELS, DEPARTMENT_LABELS } from '../../constants/enum';
import ToastMsg from '../../components/common/ToastMsg';

const UsersManager = () => {
  const { fetch, create, update, remove } = useApiMethods();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  const loadData = async () => {
    const [usersRes, rolesRes] = await Promise.all([
      fetch(USERS, {}, setLoading),
      fetch(ROLES),
    ]);
    if (usersRes?.status !== 'fail') {
      const raw = usersRes?.data;
      setUsers(Array.isArray(raw) ? raw : raw?.users || raw?.results || []);
    }
    if (rolesRes?.status !== 'fail') {
      const rawRoles = rolesRes?.data;
      setRoles(Array.isArray(rawRoles) ? rawRoles : rawRoles?.roles || rawRoles?.results || []);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (values) => {
    let res;
    if (editingUser) {
      res = await update(`${USERS}${editingUser.id}/`, values);
    } else {
      res = await create(USERS, values);
    }

    if (res?.status !== 'fail') {
      ToastMsg('success', editingUser ? 'User updated' : 'User created');
      setModalOpen(false);
      setEditingUser(null);
      form.resetFields();
      loadData();
    } else {
      ToastMsg('error', res?.message || 'Failed to save user');
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Delete User',
      content: 'Are you sure you want to delete this user?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        const res = await remove(`${USERS}${id}/`);
        if (res?.status !== 'fail') {
          ToastMsg('success', 'User deleted');
          loadData();
        }
      },
    });
  };

  const openEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue(user);
    setModalOpen(true);
  };

  const openCreate = () => {
    setEditingUser(null);
    form.resetFields();
    setModalOpen(true);
  };

  const columns = [
    { title: 'Username', dataIndex: 'login_name', key: 'login_name' },
    { title: 'First Name', dataIndex: 'login_fname', key: 'login_fname', render: (v) => v || '-' },
    { title: 'Last Name', dataIndex: 'login_lname', key: 'login_lname', render: (v) => v || '-' },
    { title: 'Email', dataIndex: 'login_email', key: 'login_email', render: (v) => v || '-' },
    {
      title: 'Role',
      dataIndex: 'role_type',
      key: 'role_type',
      render: (val) => <Tag color="blue">{ROLE_TYPE_LABELS[val] || val}</Tag>,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (val) => DEPARTMENT_LABELS[val] || val || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            type="text"
            size="small"
            icon={<Edit2 size={14} />}
            onClick={() => openEdit(record)}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<Trash2 size={14} />}
            onClick={() => handleDelete(record.id)}
          />
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">User Management</h2>
          <p className="text-muted-foreground text-sm">Manage system users and their access</p>
        </div>
        <Button
          type="primary"
          icon={<UserPlus size={16} />}
          onClick={openCreate}
          style={{ backgroundColor: '#4f46e5' }}
        >
          Add User
        </Button>
      </div>

      <Card className="rounded-xl shadow-sm">
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </Card>

      <Modal
        title={editingUser ? 'Edit User' : 'Create User'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingUser(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={editingUser ? 'Update' : 'Create'}
        okButtonProps={{ style: { backgroundColor: '#4f46e5' } }}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="login_name"
            label="Username"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input disabled={!!editingUser} placeholder="e.g. jsmith" />
          </Form.Item>
          <Form.Item name="login_fname" label="First Name">
            <Input />
          </Form.Item>
          <Form.Item name="login_lname" label="Last Name">
            <Input />
          </Form.Item>
          <Form.Item
            name="login_email"
            label="Email"
            rules={[{ type: 'email', message: 'Invalid email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="login_phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item
            name="login_password"
            label={editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
            rules={editingUser ? [] : [{ required: true, message: 'Required' }, { min: 6, message: 'Min 6 characters' }]}
            extra={editingUser ? 'Leave blank to keep the existing password.' : 'Set an initial password. The user can change it later.'}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>
          <Form.Item name="role_type" label="Role Type">
            <Select
              options={Object.entries(ROLE_TYPE_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              placeholder="Select role type"
            />
          </Form.Item>
          <Form.Item name="assigned_role_id" label="Permission Role" extra="Controls which modules this user can access (from Roles page)">
            <Select
              options={roles.map((r) => ({ value: r.id, label: r.name }))}
              placeholder="Select permission role"
              allowClear
            />
          </Form.Item>
          <Form.Item name="department" label="Department">
            <Select
              options={Object.entries(DEPARTMENT_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              placeholder="Select department"
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersManager;
